/**
 * One bank deposit that is really several payments.
 *
 * Stripe does not pay out a charge at a time. It sweeps everything that
 * cleared since the last sweep into one transfer, so a single line in the
 * Mercury account — "STRIPE; TRANSFER; COPPERRIDGE ROOFING EX", $1,588.79 —
 * can be two customers, or five, and Money In had no way to say so. It offered
 * the whole amount against one job, matched nothing, and the money sat on the
 * list.
 *
 * Jonathon, 2026-09-08, looking at exactly that deposit: it needs "the option
 * to split and choose between customers as well as if it's the first or last
 * payment".
 *
 * ## What a person types, and what is worked out
 *
 * They type the **gross** — the number beside the customer's name on the
 * Stripe payout, what the customer actually paid. Everything else follows from
 * it, because the fee is arithmetic and not a judgement:
 *
 *     fee  = 2.9% of gross + 30c        (`cardFee`, the same one everywhere)
 *     net  = gross - fee                 what reached the bank for that charge
 *
 * The parts balance when their nets add up to the deposit, to the cent. That
 * is not fussiness: the nets are what Mercury deposited, and a split that adds
 * up to something else is money recorded against a job that never arrived.
 *
 * The reference payout, which the tests pin:
 *
 *     Tyler Christiansen  $1,436.86 - $41.97 = $1,394.89
 *     Jim Messer            $200.00 -  $6.10 =   $193.90
 *                                              -----------
 *                                               $1,588.79
 *
 * ## The part that takes the rest
 *
 * One part may be marked `takesTheRest`, and it is given whatever the deposit
 * has left rather than a typed figure. It exists for the last line of a split,
 * where typing the gross and hoping the rounding lands on zero is a worse
 * experience than being handed the remainder. Its gross is worked back from
 * its net — see `grossThatNets` — so the fee is still shown, and still true.
 *
 * Nothing here writes anything or ticks anything. It is the arithmetic; the
 * confirming is `splitDeposit` in apps/web/src/lib/payments.ts, one part at a
 * time, through the same `confirmDeposit` an unsplit deposit goes through.
 */

import { cardFee } from "./mercury.js";
import { type Cents, cents, formatMoney } from "./money.js";
import { type PaymentSlot } from "./paymentReceipt.js";

/** The fewest parts a split can have. One part is not a split. */
export const MIN_SPLIT_PARTS = 2;

/**
 * What reached the bank out of one charge.
 *
 * Stripe's own arithmetic: 2.9% of the charge rounded to the cent, plus 30c.
 * Verified against the payout of 2026-09-03 — $1,436.86 gross, $41.97 fee —
 * which is where the golden test comes from.
 */
export function stripeNet(gross: Cents): Cents {
  const amount = cents(gross);
  if (amount <= 0) return 0 as Cents;
  const net = amount - cardFee(amount);
  return (net > 0 ? net : 0) as Cents;
}

/**
 * The charge that would have left this much in the bank.
 *
 * The inverse of `stripeNet`, which is not exactly invertible: rounding means
 * some nets are unreachable and some are reached by two different charges. So
 * this solves the continuous form — net = gross x 0.971 - 30c — and then walks
 * a couple of cents either way looking for an exact hit, taking the closest
 * when there is none.
 *
 * Only ever used for the part that takes the rest, where the net is the fact
 * and the gross is being shown back to a person. The net is never adjusted to
 * suit the gross; a cent of rounding is carried by the fee, which is where it
 * genuinely was.
 */
export function grossThatNets(net: Cents): Cents {
  const target = cents(net);
  if (target <= 0) return 0 as Cents;
  const start = Math.round((target + 30) / 0.971);
  let best = start;
  let bestGap = Number.MAX_SAFE_INTEGER;
  for (let gross = start - 3; gross <= start + 3; gross += 1) {
    if (gross <= 0) continue;
    const gap = Math.abs(stripeNet(cents(gross)) - target);
    if (gap < bestGap) {
      best = gross;
      bestGap = gap;
    }
    if (gap === 0) break;
  }
  return cents(best);
}

/** One customer's share of a batched deposit, as the screen holds it. */
export interface SplitPart {
  /** The job it belongs to. Null until somebody chooses one. */
  jobId: string | null;
  /**
   * Which payment it settles, when a person says. Null lets the arithmetic
   * decide, which is right whenever the amounts are distinguishable and wrong
   * on a 50/50 job where the first and the final are the same number.
   */
  slot?: PaymentSlot | null | undefined;
  /** What the customer paid, before the processor's cut. */
  grossCents?: Cents | null;
  /** Give this one whatever the deposit has left, instead of a typed figure. */
  takesTheRest?: boolean;
}

/** One part, priced. */
export interface SplitLine {
  jobId: string | null;
  slot: PaymentSlot | null;
  grossCents: Cents;
  feeCents: Cents;
  netCents: Cents;
  takesTheRest: boolean;
}

export interface SplitPlan {
  lines: SplitLine[];
  /** The deposit, less every line's net. Zero when the split balances. */
  remainderCents: Cents;
  /** True when this could be recorded: it balances and every part is whole. */
  ready: boolean;
  /** The one thing stopping it, in the words the screen uses. Null when ready. */
  problem: string | null;
}

/**
 * Price a split, and say whether it can be recorded.
 *
 * Pure. Called on every keystroke, which is what lets the screen show the
 * remainder falling to zero as somebody types.
 */
export function planDepositSplit(
  depositCents: Cents,
  parts: readonly SplitPart[],
): SplitPlan {
  const deposit = cents(depositCents);

  // Priced twice: the typed parts first, because the part that takes the rest
  // is defined as what they leave behind.
  const typed: (SplitLine | null)[] = parts.map((part) => {
    if (part.takesTheRest) return null;
    const gross = cents(Number(part.grossCents ?? 0));
    const net = stripeNet(gross);
    return {
      jobId: part.jobId ?? null,
      slot: part.slot ?? null,
      grossCents: gross,
      feeCents: cents(gross - net),
      netCents: net,
      takesTheRest: false,
    };
  });

  const typedNet = typed.reduce((sum, line) => sum + (line?.netCents ?? 0), 0);
  const left = cents(deposit - typedNet);

  const lines: SplitLine[] = parts.map((part, index) => {
    const line = typed[index];
    if (line) return line;
    // The rest. Its net is the fact and its gross is worked back from it, so
    // a cent of rounding lands on the fee rather than on the money.
    const net = (left > 0 ? left : 0) as Cents;
    const gross = grossThatNets(net);
    return {
      jobId: part.jobId ?? null,
      slot: part.slot ?? null,
      grossCents: gross,
      feeCents: cents(gross - net),
      netCents: net,
      takesTheRest: true,
    };
  });

  const remainder = cents(
    deposit - lines.reduce((sum, line) => sum + line.netCents, 0),
  );

  return {
    lines,
    remainderCents: remainder,
    ...verdict(deposit, lines, remainder),
  };
}

/**
 * Whether a priced split may be recorded, and what is wrong when it may not.
 *
 * Order matters. The complaints are checked from "this is not a split at all"
 * outwards, so a person fixing them is told the useful thing first rather than
 * the arithmetic of a half-filled form.
 */
function verdict(
  deposit: Cents,
  lines: readonly SplitLine[],
  remainder: Cents,
): { ready: boolean; problem: string | null } {
  const no = (problem: string) => ({ ready: false, problem });

  if (deposit <= 0) return no("There is nothing to split.");
  if (lines.length < MIN_SPLIT_PARTS) {
    return no("A split needs at least two payments.");
  }
  if (lines.filter((line) => line.takesTheRest).length > 1) {
    return no("Only one part can take the rest.");
  }
  if (lines.some((line) => !line.jobId)) {
    return no("Every part needs a customer.");
  }
  if (lines.some((line) => line.netCents <= 0)) {
    return no(
      "One part is too small to be a payment once the processor's fee comes out.",
    );
  }
  if (remainder > 0) {
    return no(
      `${formatMoney(remainder)} of this deposit is not accounted for yet.`,
    );
  }
  if (remainder < 0) {
    return no(
      `That is ${formatMoney(cents(-remainder))} more than the deposit. Something is too big.`,
    );
  }
  return { ready: true, problem: null };
}

/**
 * How a split reads on the job, and in the note the panel shows afterwards.
 *
 * The point of the sentence is that the number on the job is not the number on
 * the bank statement, and never will be: the customer paid the gross, the
 * processor kept its cut, the bank received the net, and all three are true at
 * once. Somebody looking at this job in a month should not have to work that
 * out again.
 */
export function splitLineNote(
  line: SplitLine,
  deposit: { amount_cents: number; posted_at?: string | null },
): string {
  const whole = cents(Number(deposit.amount_cents));
  return (
    `Part of a ${formatMoney(whole)} Stripe payout covering more than one ` +
    `customer. This customer paid ${formatMoney(line.grossCents)}; ` +
    `${formatMoney(line.feeCents)} went to the processor and ` +
    `${formatMoney(line.netCents)} reached the bank.`
  );
}
